import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {FileText, Link2, Mic, UploadCloud} from 'lucide-react';
import {ChangeEvent, FormEvent, useEffect, useRef, useState} from 'react';
import {api, signOutPursuitDeck} from '../api';
import {OnboardingState} from '../types';
import {PrimaryButton, SecondaryButton, Surface, Textarea} from '../design';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];
const MAX_BYTES = 5 * 1024 * 1024;
const AUTOSAVE_DELAY_MS = 650;
const EMPTY_ANSWER_SIGNATURE = '[]';

const OnboardingPrivacyLink = () => (
  <p className="pd-onboarding-privacy">
    Learn how the operator can access stored content, when configured services receive data, and what deletion
    does on the <a href="/privacy">privacy page</a>.
  </p>
);

const answerSignature = (answers: Record<string, string>): string =>
  JSON.stringify(Object.entries(answers).sort(([left], [right]) => left.localeCompare(right)));

type DraftStatus = 'ready' | 'dirty' | 'saving' | 'saved' | 'save_error';

export const Onboarding = ({
  onboarding,
  embedded = false,
}: {onboarding: OnboardingState; embedded?: boolean}) => {
  const Root = embedded ? 'section' : 'main';
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [hydratedRound, setHydratedRound] = useState<1 | 2 | null>(null);
  const [lastSavedSignature, setLastSavedSignature] = useState(EMPTY_ANSWER_SIGNATURE);
  const [draftStatus, setDraftStatus] = useState<DraftStatus>('ready');
  const latestAnswers = useRef<Record<string, string>>({});
  const latestRound = useRef<1 | 2 | undefined>(undefined);

  const questions = useQuery({
    queryKey: ['onboarding', 'questions'],
    queryFn: api.getQuestions,
    enabled: Boolean(onboarding.resumeDocumentId),
  });
  const activeRound = questions.data?.round;
  const answerDraft = useQuery({
    queryKey: ['onboarding', 'answer-draft', activeRound],
    queryFn: () => {
      if (!activeRound) throw new Error('An active onboarding round is required.');
      return api.getOnboardingAnswerDraft(activeRound);
    },
    enabled: Boolean(onboarding.resumeDocumentId && activeRound),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const upload = useMutation({
    mutationFn: api.uploadResume,
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
      await questions.refetch();
    },
  });

  const continueReducedQuality = useMutation({
    mutationFn: () => api.setReducedQualityMode(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
    },
  });

  const saveDraft = useMutation({
    mutationFn: ({round, currentAnswers}: {round: 1 | 2; currentAnswers: Record<string, string>}) =>
      api.saveOnboardingAnswerDraft(round, currentAnswers),
    onMutate: () => setDraftStatus('saving'),
    onSuccess: (_saved, variables) => {
      const savedSignature = answerSignature(variables.currentAnswers);
      setLastSavedSignature(savedSignature);
      if (latestRound.current === variables.round
        && answerSignature(latestAnswers.current) === savedSignature) {
        setDraftStatus('saved');
      } else {
        setDraftStatus('dirty');
      }
    },
    onError: () => setDraftStatus('save_error'),
  });

  const submitAnswers = useMutation({
    mutationFn: ({skip = false}: {skip?: boolean} = {}) =>
      api.submitAnswers(questions.data?.round ?? 1, skip ? {} : answers),
    onSuccess: async () => {
      setAnswers({});
      latestAnswers.current = {};
      setHydratedRound(null);
      setLastSavedSignature(EMPTY_ANSWER_SIGNATURE);
      setDraftStatus('ready');
      if (activeRound) {
        queryClient.removeQueries({queryKey: ['onboarding', 'answer-draft', activeRound]});
      }
      await queryClient.invalidateQueries({queryKey: ['bootstrap']});
      await questions.refetch();
    },
  });

  useEffect(() => {
    latestAnswers.current = answers;
  }, [answers]);

  useEffect(() => {
    latestRound.current = activeRound;
  }, [activeRound]);

  useEffect(() => {
    if (!activeRound) return;
    setAnswers({});
    latestAnswers.current = {};
    setHydratedRound(null);
    setLastSavedSignature(EMPTY_ANSWER_SIGNATURE);
    setDraftStatus('ready');
  }, [activeRound]);

  useEffect(() => {
    if (!activeRound
      || hydratedRound === activeRound
      || !answerDraft.data
      || answerDraft.data.round !== activeRound) {
      return;
    }
    const restored = {...answerDraft.data.answers};
    const restoredSignature = answerSignature(restored);
    setAnswers(restored);
    latestAnswers.current = restored;
    setHydratedRound(activeRound);
    setLastSavedSignature(restoredSignature);
    setDraftStatus('saved');
  }, [activeRound, answerDraft.data, hydratedRound]);

  useEffect(() => {
    if (!activeRound
      || hydratedRound !== activeRound
      || submitAnswers.isPending
      || saveDraft.isPending
      || draftStatus === 'save_error'
      || answerSignature(answers) === lastSavedSignature) {
      return;
    }
    const currentAnswers = {...answers};
    const timer = window.setTimeout(() => {
      saveDraft.mutate({round: activeRound, currentAnswers});
    }, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [
    activeRound,
    answers,
    draftStatus,
    hydratedRound,
    lastSavedSignature,
    saveDraft.isPending,
    submitAnswers.isPending,
  ]);

  const draftReady = Boolean(activeRound && hydratedRound === activeRound);
  const restoringDraft = Boolean(activeRound && !draftReady
    && (answerDraft.isPending || answerDraft.isFetching));
  const restoreFailed = Boolean(activeRound && !draftReady
    && answerDraft.isError && !answerDraft.isFetching);

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    setFileError('');
    if (!next) return setFile(null);
    if (next.size > MAX_BYTES) {
      setFile(null);
      setFileError('That file is larger than 5 MB. Choose a smaller PDF, DOCX, TXT, or Markdown file.');
      return;
    }
    if (!ACCEPTED_TYPES.includes(next.type) && !/\.(pdf|docx|txt|md|markdown)$/i.test(next.name)) {
      setFile(null);
      setFileError('Choose a PDF, DOCX, TXT, or Markdown file.');
      return;
    }
    setFile(next);
  };

  const submitFile = (event: FormEvent) => {
    event.preventDefault();
    if (file) upload.mutate(file);
  };

  const draftStatusPanel = () => {
    if (restoringDraft) {
      return (
        <div id="onboarding-autosave-status" className="pd-onboarding-autosave" data-state="loading">
          <p role="status">Restoring saved answers…</p>
        </div>
      );
    }
    if (restoreFailed) {
      return (
        <div id="onboarding-autosave-status" className="pd-onboarding-autosave" data-state="error">
          <p role="alert">Saved answers could not be restored. The fields stay locked so an older device copy is not overwritten.</p>
          <SecondaryButton type="button" onClick={() => void answerDraft.refetch()}>
            Retry restoration
          </SecondaryButton>
        </div>
      );
    }
    if (!draftReady) return null;
    if (draftStatus === 'save_error') {
      return (
        <div id="onboarding-autosave-status" className="pd-onboarding-autosave" data-state="error">
          <p role="alert">Latest changes are not saved yet. They remain on this page; retry before leaving or switching devices.</p>
          <SecondaryButton
            type="button"
            loading={saveDraft.isPending}
            onClick={() => {
              if (activeRound) {
                saveDraft.mutate({round: activeRound, currentAnswers: {...latestAnswers.current}});
              }
            }}
          >
            Retry save
          </SecondaryButton>
        </div>
      );
    }
    return null;
  };

  if (onboarding.resumeDocumentId && questions.data) {
    return (
      <Root className="pd-onboarding">
        {!embedded ? (
          <button type="button" className="pd-signout-return" onClick={() => void signOutPursuitDeck()}>
            Sign out
          </button>
        ) : null}
        <div className="pd-onboarding-header">
          <span className="pd-step">Career interview · round {questions.data.round} of 2</span>
          <h1 aria-label="Give the résumé its missing context.">
            Give the{' '}
            <span className="pd-resume-easter-egg" tabIndex={0} aria-describedby="resume-accent-joke">
              résumé
              <span id="resume-accent-joke" className="pd-resume-easter-egg-copy" role="tooltip">
                Two accents in 2026? A little typographic monocle: preppy, posh, and just arrogant
                enough to insist you noticed.
              </span>
            </span>{' '}
            its missing context.
          </h1>
          <p>
            These five bundled questions turn a document into a truthful career record. You can edit every
            fact before it becomes usable.
          </p>
        </div>
        <form
          className="pd-question-list"
          onSubmit={(event) => {
            event.preventDefault();
            if (draftReady && !saveDraft.isPending) submitAnswers.mutate({skip: false});
          }}
        >
          {draftStatusPanel()}
          {questions.data.questions.map((question, index) => (
            <Surface key={question.id} className="pd-question">
              <span className="pd-question-number">{String(index + 1).padStart(2, '0')}</span>
              <label htmlFor={'question-' + question.id}>{question.prompt}</label>
              {question.rationale ? <p>{question.rationale}</p> : null}
              <Textarea
                id={'question-' + question.id}
                autosize
                minRows={3}
                value={answers[question.id] ?? ''}
                disabled={!draftReady || submitAnswers.isPending}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setAnswers((current) => {
                    const next = {...current, [question.id]: value};
                    latestAnswers.current = next;
                    return next;
                  });
                  setDraftStatus('dirty');
                }}
                placeholder="Answer in your own words…"
              />
            </Surface>
          ))}
          {submitAnswers.isError ? (
            <p className="pd-form-error" role="alert">
              {submitAnswers.error instanceof Error ? submitAnswers.error.message : 'Answers could not be submitted.'}
            </p>
          ) : null}
          <div className="pd-form-actions">
            {questions.data.round === 2 ? (
              <SecondaryButton
                type="button"
                disabled={!draftReady || saveDraft.isPending || submitAnswers.isPending}
                onClick={() => submitAnswers.mutate({skip: true})}
              >
                Skip follow-up and continue
              </SecondaryButton>
            ) : null}
            <PrimaryButton type="submit" disabled={!draftReady || saveDraft.isPending} loading={submitAnswers.isPending}>
              Save answers and continue
            </PrimaryButton>
          </div>
          <button
            className="pd-text-link pd-onboarding-reduced"
            type="button"
            disabled={continueReducedQuality.isPending}
            onClick={() => continueReducedQuality.mutate()}
          >
            Continue with reduced-quality search for now
          </button>
          {continueReducedQuality.isError ? (
            <p className="pd-form-error" role="alert">Reduced-quality mode could not be enabled.</p>
          ) : null}
        </form>
        <OnboardingPrivacyLink />
      </Root>
    );
  }

  return (
    <Root className="pd-onboarding">
      {!embedded ? (
        <button type="button" className="pd-signout-return" onClick={() => void signOutPursuitDeck()}>
          Sign out
        </button>
      ) : null}
      <div className="pd-onboarding-header">
        <span className="pd-step">Step 1 · source material</span>
        <h1>Start with what is already true.</h1>
        <p>
          Upload your current résumé. PursuitDeck uses it as evidence—not a license to invent—and keeps every
          later claim linked to a source you can inspect.
        </p>
      </div>
      <Surface className="pd-upload-panel">
        <form onSubmit={submitFile}>
          <label className="pd-dropzone" htmlFor="resume-upload">
            <UploadCloud size={36} aria-hidden="true" />
            <strong>{file ? file.name : 'Choose your source résumé'}</strong>
            <span>PDF, DOCX, TXT, or Markdown · 5 MB maximum</span>
            <input
              id="resume-upload"
              type="file"
              accept=".pdf,.docx,.txt,.md,.markdown,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
              onChange={chooseFile}
            />
          </label>
          {fileError ? (
            <p className="pd-form-error" role="alert">
              {fileError}
            </p>
          ) : null}
          {upload.isError ? (
            <p className="pd-form-error" role="alert">
              {upload.error instanceof Error ? upload.error.message : 'The résumé could not be uploaded.'}
            </p>
          ) : null}
          <PrimaryButton type="submit" fullWidth disabled={!file} loading={upload.isPending}>
            Upload and inspect
          </PrimaryButton>
        </form>
        <div className="pd-reduced-quality-option">
          <p>You can enter the workspace without a completed profile. Search and recommendations will be less accurate until you return to finish onboarding.</p>
          <SecondaryButton
            type="button"
            loading={continueReducedQuality.isPending}
            onClick={() => continueReducedQuality.mutate()}
          >
            Continue with reduced-quality mode
          </SecondaryButton>
          {continueReducedQuality.isError ? (
            <p className="pd-form-error" role="alert">Reduced-quality mode could not be enabled.</p>
          ) : null}
        </div>
      </Surface>
      <section className="pd-input-preview" aria-label="Additional evidence supported after resume upload">
        <article>
          <FileText size={20} aria-hidden="true" />
          <h2>Documents</h2>
          <p>Project notes, performance reviews, and writing samples.</p>
        </article>
        <article>
          <Mic size={20} aria-hidden="true" />
          <h2>Voice</h2>
          <p>Record a story, review the transcript, then approve it as evidence.</p>
        </article>
        <article>
          <Link2 size={20} aria-hidden="true" />
          <h2>Public links</h2>
          <p>GitHub, LinkedIn, portfolios, or projects—with private-network addresses blocked.</p>
        </article>
      </section>
      <OnboardingPrivacyLink />
    </Root>
  );
};
